import React from 'react';
import { useB2BTranslation } from '../../B2BShared/hooks/useB2BTranslation';
import { useB2BAuth } from '../../B2BShared';
import { translatePosition } from '../../B2BShared/utils/translatePosition';

interface Business {
  businessName: string;
  businessWebsite: string;
  businessCategory: string | string[];
  businessDescription: string;
  productsServices: string;
  socialMedia: string;
  countriesOfOperation: string[];
}

interface B2BParticipantDisplayProps {
  participant: {
    name: string;
    surname: string;
    email: string;
    phoneNumber?: string;
    position: string | string[];
    linkedin?: string;
    birthday?: string;
    cityOfBirth?: string;
    businesses?: Business[];
    interestedIn?: string[];
    adminNotes?: string;
  };
}

const B2BParticipantDisplay: React.FC<B2BParticipantDisplayProps> = ({
  participant
}) => {
  const { t, tProfile, tBusinessCategory, tProfileExists } = useB2BTranslation();
  const { user } = useB2BAuth();
  
  return (
    <div className="card-content">
      {/* Personal Information */}
      <div className="personal-section">
        <h4>{tProfile('personalInformation')}</h4>
        
        {/* Name */}
        {participant.name && (
          <div className="field-display">
            <strong>{tProfile('name')}:</strong>
            <span>{participant.name}</span>
          </div>
        )}
        
        {/* Surname */}
        {participant.surname && (
          <div className="field-display">
            <strong>{tProfile('surname')}:</strong>
            <span>{participant.surname}</span>
          </div>
        )}
        
        {/* Email */}
        <div className="field-display">
          <strong>{tProfile('email')}:</strong>
          <span>
            {participant.email ? (
              <a href={`mailto:${participant.email}`}>{participant.email}</a>
            ) : t('common.notProvided')}
          </span>
        </div>
        
        {/* Phone Number */}
        {participant.phoneNumber && (
          <div className="field-display">
            <strong>{tProfile('phoneNumber')}:</strong>
            <span>
              <a href={`tel:${participant.phoneNumber}`}>{participant.phoneNumber}</a>
            </span>
          </div>
        )}
        
        {/* Position */}
        {participant.position && (Array.isArray(participant.position) ? participant.position.length > 0 : true) && (
          <div className="field-display">
            <strong>{tProfile('position')}:</strong>
            <span>
              {translatePosition(participant.position, tProfile, "positionOptions", tProfileExists)}
            </span>
          </div>
        )}
        
        {/* LinkedIn */}
        {participant.linkedin && participant.linkedin !== 'no-linkedin' && (
          <div className="field-display">
            <strong>{tProfile('linkedin')}:</strong>
            <span>
              <a href={participant.linkedin.startsWith('http') ? participant.linkedin : `https://${participant.linkedin}`} target="_blank" rel="noopener noreferrer">
                {participant.linkedin}
              </a>
            </span>
          </div>
        )}
        
      </div>

      {/* Company Information */}
      {participant.businesses && participant.businesses.length > 0 && (
        <div className="business-section">
          <h4>{tProfile('companyInformation')}</h4>
          {participant.businesses.map((business, index) => (
            <div key={index} className="business-item">
              {participant.businesses!.length > 1 && (
                <h5>{tProfile('companyName')} {index + 1}</h5>
              )}
              
              {/* Company name */}
              {business.businessName && (
                <div className="field-display">
                  <strong>{tProfile('companyName')}:</strong>
                  <span>{business.businessName}</span>
                </div>
              )}
              
              {/* Company Category */}
              {business.businessCategory && (Array.isArray(business.businessCategory) ? business.businessCategory.length > 0 : true) && (
                <div className="field-display">
                  <strong>{tProfile('companyCategory')}:</strong>
                  <span>
                    {Array.isArray(business.businessCategory)
                      ? business.businessCategory.map((c: string) => tBusinessCategory(c) || c).join(', ')
                      : tBusinessCategory(business.businessCategory)}
                  </span>
                </div>
              )}
              
              {/* Company Website */}
              {business.businessWebsite && business.businessWebsite !== 'none' && (
                <div className="field-display">
                  <strong>{tProfile('companyWebsite')}:</strong>
                  <span>
                    <a href={business.businessWebsite.startsWith('http') ? business.businessWebsite : `https://${business.businessWebsite}`} target="_blank" rel="noopener noreferrer">
                      {business.businessWebsite}
                    </a>
                  </span>
                </div>
              )}
              
              {/* Company Description */}
              {business.businessDescription && (
                <div className="field-display">
                  <strong>{tProfile('companyDescription')}:</strong>
                  <span>{business.businessDescription}</span>
                </div>
              )}
              
              {/* Countries of Operation */}
              {business.countriesOfOperation && business.countriesOfOperation.length > 0 && (
                <div className="field-display">
                  <strong>{tProfile('countriesOfOperation')}:</strong>
                  <span>
                    {business.countriesOfOperation.map((country: string) => {
                      const countryKey = `countries.${country}`;
                      let translated = tProfile(countryKey);
                      
                      const isMissing = (val: string, key: string) =>
                        val === key || val === `profile.${key}`;
                      
                      if (isMissing(translated, countryKey)) {
                        const lowerKey = `countries.${country.toLowerCase()}`;
                        const lowerTranslated = tProfile(lowerKey);
                        if (!isMissing(lowerTranslated, lowerKey)) {
                          translated = lowerTranslated;
                        }
                      }
                      
                      if (isMissing(translated, countryKey) || isMissing(translated, `countries.${country.toLowerCase()}`)) {
                        translated = country
                          .replace(/([A-Z])/g, ' $1')
                          .replace(/^./, str => str.toUpperCase())
                          .trim();
                      }
                      
                      return translated;
                    }).join(', ')}
                  </span>
                </div>
              )}
              
              {/* Company Social Media */}
              {business.socialMedia && business.socialMedia !== 'none' && (
                <div className="field-display">
                  <strong>{tProfile('companySocialMedia')}:</strong>
                  <span>{business.socialMedia}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      
      {/* Interests */}
      {participant.interestedIn && participant.interestedIn.length > 0 && (
        <div className="interests-section">
          <h4>{tProfile('interests')}</h4>
          <div className="field-display">
            <span>
              {translatePosition(participant.interestedIn, tProfile, "interestedInOptions", tProfileExists)}
            </span>
          </div>
        </div>
      )}
      
      {/* Admin Notes - Only visible to admins */}
      {user?.isAdmin && participant.adminNotes && (
        <div className="admin-notes-section">
          <div className="admin-notes-header">
            <span className="admin-icon">🔒</span>
            <strong>{t('admin.notes')}:</strong>
          </div>
          <div className="admin-notes-content">
            <div className="admin-notes-text">
              {participant.adminNotes}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default B2BParticipantDisplay;
